import EnhancedTabs from '@common/components/Tab/EnhancedTabs'
import { READER_TASK_LIST_TABS, ReaderTaskListTabsKey } from '@reader_task_list_view/constant'
import { CurrentActiveTabProvider } from '@common/context/CurrentActiveTabProvider'
import { TabOptionalComponentProvider } from '@common/context/TabOptionalComponentProvider'
import { TabConfig } from '@common/components/Tab/tabs.types'
import { useCombinedPermissions } from '@auth/hooks/useCombinedPermissions'
import useGetReadDesignDetails from '../hooks/useGetReadDesignDetails'
import { useParams } from 'react-router-dom'
import { useEffect, useState, useMemo } from 'react'

export default function ReaderTaskListTabs() {
  const { studyId } = useParams<{ studyId: string }>()

  const { hasPermission } = useCombinedPermissions()
  const { data: readDesigns, isPending: isReadDesignDataLoading } = useGetReadDesignDetails(studyId!)

  const [isForcedRead, setIsForcedRead] = useState(false)
  const [isRawDataEnabled, setRawDataEnabled] = useState(false)
  const [isOphthoEnabled, setOphthoEnabled] = useState(false)

  useEffect(() => {
    if (!isReadDesignDataLoading && readDesigns) {
      let forcedReadFound = false
      let rawDataFlagFound = false
      let ophthoFlagFound = false
      for (const element of readDesigns) {
        if (element.enable_raw_data) {
          rawDataFlagFound = true
        }
        if (element.type_of_gcis_viewer?.includes('ophtho')) {
          ophthoFlagFound = true
        }
        if (element.enable_forced_reads) {
          forcedReadFound = true
        }
      }
      setRawDataEnabled(rawDataFlagFound)
      setOphthoEnabled(ophthoFlagFound)
      setIsForcedRead(forcedReadFound)
    }
  }, [isReadDesignDataLoading, readDesigns])

  // Derive defaultTabsList based on permissions
  const defaultTabsList = useMemo(() => {
    return Object.entries(READER_TASK_LIST_TABS).reduce(
      (acc, [key, tab]) => {
        if (!tab.permission || hasPermission(tab.permission)) {
          tab.isRawDataEnabled = isRawDataEnabled
          tab.isOphthoEnabled = isOphthoEnabled
          acc[key as ReaderTaskListTabsKey] = tab
        }
        return acc
      },
      {} as Record<ReaderTaskListTabsKey, TabConfig>,
    )
  }, [hasPermission, isRawDataEnabled, isOphthoEnabled])

  // Derive filteredTabs based on isForcedRead and defaultTabsList
  const filteredTabs = useMemo(() => {
    if (isForcedRead && hasPermission(READER_TASK_LIST_TABS.MY_TASKS.permission!)) {
      return { MY_TASKS: READER_TASK_LIST_TABS.MY_TASKS } as Record<ReaderTaskListTabsKey, TabConfig>
    }
    return defaultTabsList
  }, [isForcedRead, hasPermission, defaultTabsList])

  return (
    !isReadDesignDataLoading && (
      <CurrentActiveTabProvider>
        <TabOptionalComponentProvider>
          {Object.keys(filteredTabs).length > 0 && <EnhancedTabs tabs={filteredTabs} searchEnabled />}
        </TabOptionalComponentProvider>
      </CurrentActiveTabProvider>
    )
  )
}
